/**
 * normalizeNavLayout — runtime Layer-3 counterpart to validate.cjs nav rules.
 *
 * Layer 2 (validator, `.claude/skills/cogo-ui/tools/validate.cjs`) flags:
 *   1. nav.logoSection-w-full  (ERROR)   — logo/brand containers inside a nav
 *                                          ancestor must use shrink-0, not
 *                                          w-full (otherwise the logo eats
 *                                          the whole row).
 *   2. nav.layout-not-grid     (WARNING) — the 3-slot nav container must use
 *                                          `grid grid-cols-[auto_1fr_auto]`;
 *                                          `flex items-center justify-between`
 *                                          collapses the center slot onto one
 *                                          side.
 *
 * This module rewrites offending class strings in-place on a cloned UI tree
 * so existing templates render correctly even before source-level fixes land.
 * Source of truth remains the ui.json — this is a safety net for drift.
 *
 * The module is pure — no React, no side effects. It is called from
 * `app-runtime.ts` every time a block enters the runtime.
 *
 * Layer 2 ≡ Layer 3 pact:
 *   - If the validator flags X, the normalizer fixes X.
 *   - If the validator allows Y, the normalizer leaves Y alone.
 *   - Detection predicates (nav-ancestor detection, slot-container shape,
 *     logo-id matching) are byte-identical copies of validate.cjs
 *     `LOGO_SECTION_ID_RE` / `NAV_ANCESTOR_ID_RE` / the `-(?:nav|topnav|header)$`
 *     suffix pattern. Update in lockstep.
 */

// Logo/brand container ids that commonly appear as the "left slot" of a nav
// bar. Match is case-insensitive and anchored to the full id — same regex
// validate.cjs uses for `LOGO_SECTION_ID_RE`.
const LOGO_SECTION_ID_RE =
  /^(?:nav-left|brand-section|logo-container|topnav-left|brand-wrapper|brand-container|header-left)$/i

// Ancestor nav-root detection — id suffix patterns observed across the 47
// templates. Mirrors validate.cjs `NAV_ANCESTOR_ID_RE`.
const NAV_ANCESTOR_ID_RE =
  /(?:^|-)(?:nav|topnav|shop-header|admin-topnav|pm-topnav|dash-topnav|main-nav)$/i

// "Is this node a nav parent whose first container child holds the 3-slot
// row?" Mirrors validate.cjs rule 2 predicate: type==="nav" OR id ending
// in -nav / -topnav / -header.
const NAV_PARENT_ID_RE = /-(?:nav|topnav|header)$/i

// Detect `flex items-center justify-between` — all three tokens present.
// Detect `grid grid-cols-[auto_1fr_auto]` — signal the contract shape is
// already in place, in which case we leave the className alone.
const FLEX_TOKEN_RE = /\bflex\b/
const ITEMS_CENTER_RE = /\bitems-center\b/
const JUSTIFY_BETWEEN_RE = /\bjustify-between\b/
const GRID_RE = /\bgrid\b/
const GRID_COLS_NAV_RE = /grid-cols-\[auto_1fr_auto\]/

export interface UiNode {
  id?: string
  type?: string
  className?: string
  children?: UiNode[]
  // Other fields pass through untouched.
  [key: string]: unknown
}

export interface NormalizationStats {
  /** nav.layout-not-grid auto-fixes applied */
  layoutRewrites: number
  /** nav.logoSection-w-full auto-fixes applied */
  logoRewrites: number
}

/**
 * Token-level rewrite: `flex items-center justify-between` →
 * `grid grid-cols-[auto_1fr_auto] items-center`, preserving all other tokens.
 *
 * Edge case: when the className also contains one of the rewrite targets
 * already (e.g. `grid`), we still rewrite the `flex justify-between` pair —
 * the validator only passes when `grid-cols-[auto_1fr_auto]` is present, so
 * a bare `grid` without the cols spec still counts as the "flex" shape.
 */
function rewriteNavLayoutClassName(className: string): string {
  const tokens = className.split(/\s+/).filter(Boolean)
  const out: string[] = []
  let droppedFlex = false
  let droppedJustifyBetween = false
  for (const t of tokens) {
    if (t === 'flex') {
      droppedFlex = true
      continue
    }
    if (t === 'justify-between') {
      droppedJustifyBetween = true
      continue
    }
    // Drop any existing `grid-cols-*` variant — we re-insert the canonical
    // one. `items-center` is preserved via the default branch.
    out.push(t)
  }
  // Only emit the rewrite when we actually saw the flex shape. This keeps
  // the function idempotent: calling it on an already-compliant className
  // is a no-op.
  if (!droppedFlex || !droppedJustifyBetween) return className
  const hasGrid = out.some((t) => t === 'grid')
  const hasItemsCenter = out.some((t) => t === 'items-center')
  const additions: string[] = []
  if (!hasGrid) additions.push('grid')
  additions.push('grid-cols-[auto_1fr_auto]')
  if (!hasItemsCenter) additions.push('items-center')
  return [...additions, ...out].join(' ')
}

/**
 * Token-level rewrite: strip `w-full`, add `shrink-0` if missing. Preserves
 * everything else — idempotent on compliant input.
 */
function rewriteLogoSectionClassName(className: string): string {
  const tokens = className.split(/\s+/).filter(Boolean)
  const hadWFull = tokens.includes('w-full')
  if (!hadWFull) return className
  const filtered = tokens.filter((t) => t !== 'w-full')
  if (!filtered.includes('shrink-0')) filtered.push('shrink-0')
  return filtered.join(' ')
}

function isNavAncestor(node: UiNode | undefined): boolean {
  if (!node) return false
  if (node.type === 'nav') return true
  if (typeof node.id === 'string' && NAV_ANCESTOR_ID_RE.test(node.id)) return true
  return false
}

function isNavParent(node: UiNode): boolean {
  if (node.type === 'nav') return true
  if (typeof node.id === 'string' && NAV_PARENT_ID_RE.test(node.id)) return true
  return false
}

/**
 * Walk the ui tree once, ancestor-aware, and rewrite offending classNames
 * in place. Returns counters for observability/tests.
 *
 * The input tree is mutated in place — callers that need immutability should
 * clone before passing.
 */
export function normalizeNavLayout(
  root: UiNode | null | undefined,
): NormalizationStats {
  const stats: NormalizationStats = { layoutRewrites: 0, logoRewrites: 0 }
  if (!root || typeof root !== 'object') return stats

  const walk = (node: UiNode, ancestors: UiNode[]) => {
    if (!node || typeof node !== 'object') return

    // --- Rule 1: nav.logoSection-w-full ---
    if (typeof node.id === 'string' && LOGO_SECTION_ID_RE.test(node.id)) {
      const cls = typeof node.className === 'string' ? node.className : ''
      if (/\bw-full\b/.test(cls) && ancestors.some(isNavAncestor)) {
        const next = rewriteLogoSectionClassName(cls)
        if (next !== cls) {
          node.className = next
          stats.logoRewrites++
        }
      }
    }

    // --- Rule 2: nav.layout-not-grid ---
    // A nav parent's first container child with 2–3 children is the slot row.
    // Rewrite that child's className, not the parent's — the parent (type:nav
    // or *-topnav container) usually carries sticky/positioning classes, not
    // the layout tokens.
    if (isNavParent(node) && Array.isArray(node.children)) {
      const slotContainer = node.children.find(
        (c) => c && c.type === 'container' && Array.isArray(c.children),
      ) as UiNode | undefined
      if (
        slotContainer &&
        Array.isArray(slotContainer.children) &&
        slotContainer.children.length >= 2 &&
        slotContainer.children.length <= 3
      ) {
        const cls =
          typeof slotContainer.className === 'string' ? slotContainer.className : ''
        const hasFlexBetween =
          FLEX_TOKEN_RE.test(cls) &&
          ITEMS_CENTER_RE.test(cls) &&
          JUSTIFY_BETWEEN_RE.test(cls)
        const hasGridCols = GRID_RE.test(cls) && GRID_COLS_NAV_RE.test(cls)
        if (hasFlexBetween && !hasGridCols) {
          const next = rewriteNavLayoutClassName(cls)
          if (next !== cls) {
            try {
              slotContainer.className = next
              stats.layoutRewrites++
            } catch {
              // Frozen / read-only input (e.g. Vite-imported JSON corpus
              // example bundled by Channel 2 mock-stream). Skip the legacy
              // layout-token rewrite for this slot — runtime renders the
              // original className faithfully.
            }
          }
        }
      }
    }

    if (Array.isArray(node.children)) {
      const nextAncestors = ancestors.concat([node])
      for (const child of node.children) walk(child, nextAncestors)
    }
  }

  walk(root, [])
  return stats
}
